import React, { ReactNode, createContext, useCallback, useContext, useMemo, useState } from 'react'

export interface TableExportRegistration {
  id: string
  onExport: () => void | Promise<void>
  isExporting: boolean
  disabled: boolean
}

interface TableExportContextValue {
  registration: TableExportRegistration | null
  registerTableExport: (registration: TableExportRegistration) => void
  unregisterTableExport: (id: string) => void
  hasPageHeader: boolean
  registerPageHeader: () => void
  unregisterPageHeader: () => void
}

const TableExportContext = createContext<TableExportContextValue | null>(null)

export const TableExportProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [registration, setRegistration] = useState<TableExportRegistration | null>(null)
  const [headerCount, setHeaderCount] = useState(0)

  const registerTableExport = useCallback((next: TableExportRegistration) => {
    setRegistration(next)
  }, [])

  const unregisterTableExport = useCallback((id: string) => {
    setRegistration((current) => (current?.id === id ? null : current))
  }, [])

  const registerPageHeader = useCallback(() => {
    setHeaderCount((count) => count + 1)
  }, [])

  const unregisterPageHeader = useCallback(() => {
    setHeaderCount((count) => Math.max(0, count - 1))
  }, [])

  const value = useMemo(
    () => ({
      registration,
      registerTableExport,
      unregisterTableExport,
      hasPageHeader: headerCount > 0,
      registerPageHeader,
      unregisterPageHeader,
    }),
    [registration, registerTableExport, unregisterTableExport, headerCount, registerPageHeader, unregisterPageHeader],
  )

  return <TableExportContext.Provider value={value}>{children}</TableExportContext.Provider>
}

export const useTableExportContext = () => useContext(TableExportContext)
